import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App,
  Button,
  Empty,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Tag,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { CircleCheck, Plus, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { DomainRule } from 'src/api/facade/types'
// eslint-disable-next-line no-restricted-imports -- P6 (86caeu9ma): domain rules still on mock; no BE domain endpoint exists yet
import { mockQueryKeys, mockWorkspaceApi } from 'src/api/mock'
import {
  ProTableContainer,
  QueryErrorAlert,
  SoonTag,
  TableActionColumnTitle,
  TableActions
} from 'src/components'
import { useShowApiErrors } from 'src/utils'

import { WorkspacePageShell, useWorkspaceRoute } from './WorkspacePageShell'

export const WorkspaceDomainsPage = () => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const queryClient = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [form] = Form.useForm<{ domain: string; mode: DomainRule['mode'] }>()
  const { workspaceId, idValid } = useWorkspaceRoute()

  const {
    data: rules = [],
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: mockQueryKeys.workspaceDomains(workspaceId),
    queryFn: () => mockWorkspaceApi.listDomainRules(workspaceId),
    enabled: idValid
  })

  const invalidateDomainsAndAudit = () => {
    void queryClient.invalidateQueries({
      queryKey: mockQueryKeys.workspaceDomains(workspaceId)
    })
    void queryClient.invalidateQueries({
      queryKey: mockQueryKeys.workspaceAudit(workspaceId)
    })
  }

  const createMutation = useMutation({
    mutationFn: (values: { domain: string; mode: DomainRule['mode'] }) =>
      mockWorkspaceApi.createDomainRule(workspaceId, {
        domain: values.domain.trim(),
        mode: values.mode
      }),
    onSuccess: () => {
      invalidateDomainsAndAudit()
      message.success(t('workspace.domains.createSuccess'))
      setAddOpen(false)
      form.resetFields()
    },
    onError: (e) => showApiErrors(e)
  })

  const verifyMutation = useMutation({
    mutationFn: (ruleId: string) =>
      mockWorkspaceApi.verifyDomainRule(workspaceId, ruleId),
    onSuccess: () => {
      invalidateDomainsAndAudit()
      message.success(t('workspace.domains.verifiedSuccess'))
    },
    onError: (e) => showApiErrors(e)
  })

  const deleteMutation = useMutation({
    mutationFn: (ruleId: string) =>
      mockWorkspaceApi.deleteDomainRule(workspaceId, ruleId),
    onSuccess: () => {
      invalidateDomainsAndAudit()
      message.success(t('workspace.domains.deletedSuccess'))
    },
    onError: (e) => showApiErrors(e)
  })

  const columns: ColumnsType<DomainRule> = useMemo(
    () => [
      {
        fixed: 'left',
        title: t('workspace.domains.columns.domain'),
        dataIndex: 'domain',
        key: 'domain'
      },
      {
        title: t('workspace.domains.columns.mode'),
        dataIndex: 'mode',
        key: 'mode',
        render: (mode: DomainRule['mode']) =>
          t(`workspace.domains.mode.${mode}`)
      },
      {
        title: t('workspace.domains.columns.verified'),
        dataIndex: 'verified',
        key: 'verified',
        render: (verified: boolean) => (
          <Tag color={verified ? 'green' : 'orange'}>
            {verified
              ? t('workspace.domains.verified.yes')
              : t('workspace.domains.verified.no')}
          </Tag>
        )
      },
      {
        title: <TableActionColumnTitle />,
        key: 'actions',
        width: 80,
        align: 'center',
        render: (_: unknown, record: DomainRule) => (
          <TableActions
            options={[
              ...(!record.verified
                ? [
                    {
                      key: 'verify',
                      icon: <CircleCheck size={16} />,
                      label: t('workspace.domains.verifyDomain'),
                      onClick: () => verifyMutation.mutate(record.id)
                    }
                  ]
                : []),
              {
                key: 'delete',
                icon: <Trash2 size={16} />,
                label: t('workspace.domains.deleteDomain'),
                danger: true,
                onClick: () => deleteMutation.mutate(record.id)
              }
            ]}
          />
        )
      }
    ],
    [t, verifyMutation, deleteMutation]
  )

  return (
    <WorkspacePageShell
      sectionTitle={t('workspace.domains.sectionTitle')}
      sectionDescription={t('workspace.domains.sectionDescription')}
    >
      <Flex orientation="vertical" gap={16} style={{ width: '100%' }}>
        {isError ? <QueryErrorAlert onRetry={() => void refetch()} /> : null}
        <ProTableContainer<DomainRule>
          showSearch={false}
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={rules}
          locale={{
            emptyText: <Empty description={t('workspace.domains.empty')} />
          }}
          pagination={false}
          leftChildren={
            <Flex align="center" gap={4}>
              <Typography.Text strong>
                {t('workspace.domains.tableTitle')}
              </Typography.Text>
              <SoonTag />
            </Flex>
          }
          rightChildren={
            <Button
              type="primary"
              icon={<Plus size={16} />}
              onClick={() => setAddOpen(true)}
            >
              {t('workspace.domains.addDomain')}
            </Button>
          }
        />
      </Flex>

      <Modal
        title={t('workspace.domains.addModalTitle')}
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        okText={t('save')}
        confirmLoading={createMutation.isPending}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ mode: 'allow_invite' as DomainRule['mode'] }}
          onFinish={(values) => createMutation.mutate(values)}
        >
          <Form.Item
            name="domain"
            label={t('workspace.domains.domainLabel')}
            rules={[{ required: true }]}
          >
            <Input placeholder="example.com" />
          </Form.Item>
          <Form.Item
            name="mode"
            label={t('workspace.domains.modeLabel')}
            rules={[{ required: true }]}
          >
            <Select
              options={(
                ['allow_invite', 'auto_join', 'claim_required'] as const
              ).map((m) => ({
                value: m,
                label: t(`workspace.domains.mode.${m}`)
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </WorkspacePageShell>
  )
}
